import { Grid, Tooltip } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Column } from "react-table";
import styled from "styled-components";
import { Heading, Title } from "../../components/styled";

import {
	PrimaryButtonWithoutIcon,
	SecondaryButton,
} from "../../components/Buttons";
import { WalletInfo } from "../../components/WalletSideBar";
import { WalletBalanceChart } from "../../components/charts/WalletBalanceChart";
import { ManualDistributionForm } from "../../components/modals/ManualDistributionForm";
import { DistributionModal } from "../../components/modals/ManualDistributionModal";
import { WalletTransferModal } from "../../components/modals/WalletTransferModal";
import { TransactionsTable } from "../../components/tables/PaginatedTable";
import { useTokenDetails } from "../../context/token-details-context";
import { useGetTransactionHistory } from "../../hooks/useWallets";
import { device } from "../../lib/constants";
import type { Transaction } from "../../services/wallet-service";
import { getTransactionsTableColumnConfig } from "./table-config";

const Wrapper = styled.div`
  padding: 0 64px;
  padding-bottom: 40px;
  @media ${device.tablet} {
    padding: 0;
    margin: 0;
  }
`;

const PageGrid = styled(Grid)`
  @media ${device.tablet} {
    padding: 0;
    margin: 0;
  }
`;

const Header = styled.div`
  margin-top: 40px;
`;

const LeftSection = styled(Grid.Col)`
  @media ${device.tablet} {
    padding: 0 16px;
  }
`;

const RightSideBar = styled(Grid.Col)`
  background: var(--accentBg);
  @media ${device.tablet} {
    padding: 0 16px;
  }
`;

const columnPropGetter = (col: Column<Transaction>) => {
	const { id } = col;
	let textAlign: "start" | "end";
	switch (id) {
		case "Transaction ID":
			textAlign = "end";
			break;
		default:
			textAlign = "start";
			break;
	}
	return {
		style: {
			textAlign,
		},
	};
};

export const Wallets = (): JSX.Element => {
	const [modalOpened, setModalOpened] = useState<boolean>(false);
	const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
	const [_, setShowSuccessModal] = useState<boolean>(false);
	const { t } = useTranslation();
	const [showWalletTransferModal, setShowWalletTransferModal] =
		useState<boolean>(false);

	useDocumentTitle(`DAP: ${t("wallets.title")}`);

	const {
		tokenDetails: walletBalanceAndTokenDetails,
		isLoadingWalletTokenDetails,
		walletSummaryDetails,
	} = useTokenDetails();
	const tokenSymbol = walletBalanceAndTokenDetails?.[0].symbol;
	const wallets = walletSummaryDetails?.wallets || [];

	const distributionWallet = wallets?.find(
		(wallet) => wallet?.category === "Distribution",
	);

	const [queryPageIndex, setQueryPageIndex] = useState(0);
	const [queryPageSize, setQueryPageSize] = useState(5);

	const {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		data = [] as any,
		isLoading: isLoadingTransactions,
		isFetching,
		isPreviousData,
	} = useGetTransactionHistory(
		distributionWallet?.id,
		queryPageIndex,
		queryPageSize,
	);

	const transactions = data?.transactions || [];

	const toolTipStyles = {
		body: {
			fontFamily: "ProximaNova",
			fontSize: 16,
			lineHeight: "22px",
      backgroundColor: "var(--accentBg)",
      color: 'var(--textColor)',
		},
	};
	return (
		<Wrapper>
			<PageGrid gutter={64}>
				<LeftSection md={12} lg={8}>
					<Header>
						<Heading>Distribution Wallet Overview</Heading>
						<ButtonContainer>
							<Tooltip
								wrapLines
								label="Fund distribution wallet from master wallet"
								withArrow
								styles={toolTipStyles}
								width={280}
							>
								<FundButton
									title="Fund Wallet"
									onClick={() => {
										setShowWalletTransferModal(true);
									}}
								/>
							</Tooltip>

							<Tooltip
								label="Distribute tokens from Distribution wallet to institutional or end-user wallet"
								withArrow
								styles={toolTipStyles}
								width={280}
								wrapLines
							>
								<DistributeButton
									title={`${t("distribute.tokens")}`}
									onClick={() => setModalOpened(true)}
								/>
							</Tooltip>
						</ButtonContainer>
					</Header>
					<WalletBalanceChart />
					<RecentTransactionsArea>
						<Title style={{ textTransform: "none" }}>
							{t("navigation.transactions")}
						</Title>
						<TransactionsTable<Transaction>
							columnConfig={getTransactionsTableColumnConfig(t, tokenSymbol)}
							loading={
								isLoadingTransactions ||
								isFetching ||
								isLoadingWalletTokenDetails
							}
							totalPages={data?.totalPages}
							rowData={transactions}
							getColumnProps={columnPropGetter}
							hideFilters
							queryPageIndex={queryPageIndex}
							queryPageSize={queryPageSize}
							setQueryPageIndex={setQueryPageIndex}
							setQueryPageSize={setQueryPageSize}
							totalItems={data?.totalItems}
							isPreviousData={isPreviousData}
						/>
					</RecentTransactionsArea>
				</LeftSection>
				<RightSideBar md={12} lg={4}>
					<Header>
						<SummaryHeading>{t("supply.summary")}</SummaryHeading>
					</Header>
					<WalletInfo />
				</RightSideBar>
			</PageGrid>

			<DistributionModal
				isVisible={modalOpened}
				setIsVisible={setModalOpened}
				manualDistributeCallback={() => {
					setFormModalOpened(true);
				}}
			/>
			<ManualDistributionForm
				isVisible={formModalOpened}
				setIsVisible={setFormModalOpened}
				callback={() => setShowSuccessModal(true)}
			/>
			<WalletTransferModal
				isVisible={showWalletTransferModal}
				setIsVisible={setShowWalletTransferModal}
			/>
		</Wrapper>
	);
};

const SummaryHeading = styled.h2`
  font-size: 20px;
  font-family: 'ProximaNova', sans-serif;
  line-height: 30px;
  color: var(--textColor);
`;
const FundButton = styled(PrimaryButtonWithoutIcon)`
  max-width: 146px;
  margin-right: 24px;
  height: 40px;
`;

const DistributeButton = styled(SecondaryButton)`
  max-width: 180px;
  height: 40px;
`;
const RecentTransactionsArea = styled.div`
  margin-top: 50px;
`;

const ButtonContainer = styled.div`
  margin-top: 22px;
  display: flex;
  align-items: center;
`;
